import React, { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import PromptDrawer from '../components/Prompts/PromptDrawer';
import PromptListDrawer from '../components/Prompts/PromptListDrawer';
import { AnalyticsEvent, useAnalytics } from '../components/Providers/AnalyticsProvider';
import { useDrawer } from '../components/Providers/DrawerProvider';
import { findFeaturedPrompts } from '../redux/actions/promptActions';
import { AppDispatch } from '../redux/store';
import { DrawerTypes, PromptDrawerMode, PromptView } from '../Types/enums';
import { Prompt } from '../Types/prompt';

export type UsePromptDrawerProps = {
  promptView?: PromptView | undefined;
  isAIAppStore?: boolean;
  autoOpenFromUrl?: boolean;
  shouldRefetchFeaturedPrompts?: boolean;
  drawerType?: DrawerTypes;
  onNewPrompt?: (prompt: Prompt) => void;
  onEditPrompt?: (prompt: Prompt) => void;
};

/**
 * Custom hook to manage the behavior of a prompt drawer, including opening the drawer
 * and handling URL-based prompt ID navigation.
 *
 * @param {React.ReactNode} props.promptView - The prompt view state to be set on the store.
 * @param {boolean} [props.autoOpenFromUrl=false] - Whether to automatically open the drawer if a `promptId` is present in the URL.
 * @param {DrawerTypes} [props.drawerType=DrawerTypes.SINGLE_PROMPT] - Controls if the drawer opened is the prompt drawer or the prompt list drawer.
 * @param {Function} props.onNewPrompt - Callback function triggered when a new prompt is created.
 * @param {Function} props.onEditPrompt - Callback function triggered when an existing prompt is edited.
 *
 * @returns {Object} - An object containing the `openPromptDrawer` function.
 * @returns {Function} openPromptDrawer - Function to open the prompt drawer, optionally with a specific prompt ID.
 */
export const usePromptDrawer = ({
  promptView,
  isAIAppStore = false,
  autoOpenFromUrl = false,
  shouldRefetchFeaturedPrompts = false,
  drawerType = DrawerTypes.SINGLE_PROMPT,
  onNewPrompt,
  onEditPrompt,
}: UsePromptDrawerProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { openDrawer, addOnClose } = useDrawer();
  const searchParams = new URLSearchParams(location.search);
  const promptIdFromUrl = searchParams.get('promptId') ?? undefined;
  const analytics = useAnalytics();

  const handleClose = useCallback(() => {
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.delete('promptId');
    navigate({ search: newSearchParams.toString() }, { replace: true });
    if (shouldRefetchFeaturedPrompts) dispatch(findFeaturedPrompts());
  }, [shouldRefetchFeaturedPrompts, dispatch, navigate]);

  // Open the prompt drawer by setting or overriding the prompt ID in the URL
  const openPromptDrawer = useCallback(
    (id?: string) => {
      if (id && drawerType === DrawerTypes.SINGLE_PROMPT) {
        analytics.event(
          AnalyticsEvent.WM_NIGEL_OPEN_PROMPT_DRAWER,
          {
            id: id,
          },
          {
            promptId: id,
            promptView: promptView,
          }
        );

        // Update the URL with the promptId
        const newSearchParams = new URLSearchParams(location.search);
        newSearchParams.set('promptId', id);
        navigate({ search: newSearchParams.toString() });

        addOnClose(handleClose);
      } else if (drawerType === DrawerTypes.SINGLE_PROMPT) {
        // Open the drawer to create a new prompt
        openDrawer({
          anchor: 'right',
          content: (
            <PromptDrawer
              mode={isAIAppStore ? PromptDrawerMode.Create : PromptDrawerMode.Builder}
              promptId={undefined}
              promptView={promptView}
              isAIAppStore={isAIAppStore}
              onNewPrompt={onNewPrompt}
            />
          ),
        });
      } else if (drawerType === DrawerTypes.PROMPT_LIST) {
        openDrawer({
          anchor: 'right',
          content: <PromptListDrawer />,
        });
      } else {
        console.error('Tried to open an invalid drawer type');
      }
    },
    [
      promptView,
      addOnClose,
      navigate,
      onNewPrompt,
      openDrawer,
      analytics,
      handleClose,
      drawerType,
      isAIAppStore,
    ]
  );

  const onEdit = useCallback(
    (prompt: Prompt) => {
      // Update the URL  with the new prompt version id, witch will eventually update the drawer content
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.set('promptId', prompt.id);
      navigate({ search: newSearchParams.toString() });
      onEditPrompt?.(prompt);
    },
    [navigate, onEditPrompt]
  );

  // Automatically open the drawer if `promptId` is in the URL and `autoOpenFromUrl` is true
  useEffect(() => {
    if (!(autoOpenFromUrl && drawerType === DrawerTypes.SINGLE_PROMPT)) return;

    if (promptIdFromUrl) {
      openDrawer({
        anchor: 'right',
        content: (
          <PromptDrawer
            promptId={promptIdFromUrl}
            promptView={promptView}
            isAIAppStore={isAIAppStore}
            onNewPrompt={onNewPrompt}
            onEditPrompt={onEdit}
          />
        ),
      });
      addOnClose(handleClose);
    }
  }, [
    promptIdFromUrl,
    autoOpenFromUrl,
    promptView,
    onNewPrompt,
    onEdit,
    openDrawer,
    addOnClose,
    handleClose,
    drawerType,
    isAIAppStore,
  ]);

  return { openPromptDrawer };
};
